<template>
  <HeadTitle
    title="Flujos de aprobación"
    description="Listado de flujos de aprobación del grupo de configuración"
  />
  <Toolset
    ref="toolset"
    :searcher="searcher"
    add-button-icon="pi pi-plus"
    add-button-label="Crear nuevo flujo de aprobación"
    :to-add="toAdd"
    class="mt-5"
  />
  <br />
  <DataTable
    class="tabla mb-5"
    :value="approvalFlows.data"
    paginator
    :rows="pagination.limit"
    :rowsPerPageOptions="[5, 10, 20, 50]"
    tableStyle="min-width: 50rem"
    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
    :totalRecords="approvalFlows.totalItems"
    :currentPageReportTemplate="currentPageReportTemplate"
    scrollable
    scrollHeight="600px"
    @page="onPageChange"
    sortField="updatedAt"
    :sortOrder="-1"
    :loading="isLoading"
  >
    <template #empty> No se encontraron flujos de aprobación. </template>
    <Column field="description" header="Descripción">
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
    </Column>
    <Column field="onlyPayrolls" sortable header="Cant. Nóminas">
      <template #body="{ data, field }">
        {{ data[field].length }}
      </template>
    </Column>
    <Column field="onlyEmployees" sortable header="Cant. Empleados">
      <template #body="{ data, field }">
        {{ data[field].length }}
      </template>
    </Column>
    <Column field="numberSteps" sortable header="Cant. Pasos">
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
    </Column>
    <Column field="updatedAt" header="Última actualización" sortable>
      <template #body="{ data, field }">
        <span v-local-date="data[field]"></span>
      </template>
    </Column>
    <Column header="Acciones">
      <template #body="{ data }">
        <ConfirmPopup></ConfirmPopup>
        <div class="actions">
          <Button
            v-tooltip.bottom="'Ver detalles'"
            class="action-btn view p-button-sm mr-2"
            icon="pi pi-eye"
            @click="goToDetails(data['id'])"
          />
          <Button
            class="action-btn view p-button-sm"
            icon="pi pi-trash"
            @click="deleteApprovalFlow($event, data['id'])"
            severity="danger"
            v-tooltip.bottom="'Eliminar'"
          />
        </div>
      </template>
    </Column>
  </DataTable>
  <Footer>
    <template #left>
      <AuditButton
        :only-deleted="true"
        :root-id="payrollConfigId"
        :label="'Ver registros eliminados'"
        :entities="['ApprovalFlow']"
      />
    </template>
    <Button
      class="p-button-text text-sm font-light text-primary"
      icon="pi pi-angle-left"
      label="Volver"
      severity="secondary"
      @click="router.push('/payrollsConfig/')"
    />
  </Footer>
</template>
<script lang="ts" setup>
  import { computed, onMounted, reactive, ref } from 'vue'
  import { useRoute } from 'vue-router'
  import {
    getApprovalFlowsByPayrollConfigId,
    deleteApprovalFlowId,
  } from '../services/ApprovalFlowService'
  import ApprovalFlow from '../models/ApprovalFlow'
  import router from '@/router'
  import Footer from '@/shared/components/Footer.vue'
  import Toolset from '@/shared/components/Toolset.vue'
  import { useConfirm } from 'primevue/useconfirm'
  import useNotifications from '@/store/notification'
  import useCompany from '@/store/company'
  import { getPayrollConfigDetail } from '@/modules/payrolls-config/services/payrollsConfigService'
  import AuditButton from '@/shared/components/AuditButton.vue'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import ProgressSpinner from 'primevue/progressspinner'

  const payrollConfigId = ref()
  const toolset = ref()
  const confirm = useConfirm()
  const notificationStore = useNotifications()
  const companyStore = useCompany()
  const isLoading = ref(false)

  const pagination = reactive({
    currentPage: 0,
    lastPage: 0,
    limit: 5,
  })

  const approvalFlows = reactive({
    data: [] as ApprovalFlow[],
    totalItems: 0,
  })

  const path = useRoute()

  onMounted(async () => {
    const { id } = path.params
    payrollConfigId.value = id
    await refreshData()
  })

  const toAdd = () => {
    router.push(
      '/payrollsConfig/' + payrollConfigId.value + '/approvalFlows/add'
    )
  }

  const searcher = async () => {
    const { id } = path.params
    isLoading.value = true
    const response = await getApprovalFlowsByPayrollConfigId(
      String(id),
      toolset.value.search
    )
    isLoading.value = false
    approvalFlows.data = response.data
    approvalFlows.totalItems = response.totalItems
  }

  const refreshData = async () => {
    const { id } = path.params
    isLoading.value = true
    const [approvalFlowResponse, payrollConfig] = await Promise.all([
      getApprovalFlowsByPayrollConfigId(String(id)),
      getPayrollConfigDetail(String(id)),
    ])
    isLoading.value = false

    companyStore.setCompany({
      id: payrollConfig.company.id,
      companyId: payrollConfig.company.companyId,
      code: payrollConfig.company.code,
      name: payrollConfig.company.name,
      client: {
        id: payrollConfig.client.id,
        code: payrollConfig.client.code,
        name: payrollConfig.client.name,
      },
    })

    approvalFlows.data = approvalFlowResponse.data
    approvalFlows.totalItems = approvalFlowResponse.totalItems
  }

  const onPageChange = async (event: any) => {
    const { first, rows } = event
    pagination.limit = rows
    pagination.currentPage = Math.floor(first / rows + 1)
    pagination.lastPage = Math.floor(
      approvalFlows.totalItems / pagination.limit
    )
  }

  const goToDetails = async (id: string) => {
    const { id: payrollConfigId } = path.params
    router.push(
      '/payrollsConfig/' + payrollConfigId + '/approvalFlows/detail/' + id
    )
  }

  const deleteApprovalFlow = async (event: any, id: string) => {
    confirm.require({
      target: event.currentTarget,
      message: '¿Realmente quieres eliminar este registro?',
      icon: 'pi pi-info-circle',
      acceptClass: 'p-button-danger',
      acceptLabel: 'aceptar',
      rejectLabel: 'cancelar',
      accept: async () => {
        const response = await deleteApprovalFlowId(id)
        if (response === 'OK') {
          notificationStore.addSuccesNotification({
            detail: `200`,
            severity: 'success',
            summary: `Compañia eliminada con exito.`,
          })
          await refreshData()
        }
      },
      reject: () => {},
    })
  }

  const currentPageReportTemplate = computed(() => {
    return `{first} a {last} de ${approvalFlows.totalItems}`
  })
</script>
<style scoped>
  .actions {
    display: flex;
    width: auto;
    flex-direction: row;
  }

  .action-btn {
    height: 20px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
</style>
